import {Helmet} from '@dr.pogodin/react-helmet';
import {DataStatus} from 'platform/components';
import {ZodSchema} from 'zod';

import {useCallback, useEffect, useRef, useState} from 'react';

import {omit} from 'ramda';

import {
  ConditionEnum,
  useCreateVehicleMutation,
  useRegisterVehicleForSaleMutation,
  useRegisterVehicleToBuyMutation,
  VehicleTypeEnum,
} from '@dms/api/vehicle';
import {
  SaleVehicle,
  SaleVehicleValidationSchema,
  toAPIVehicle,
  useAdditionalNote,
  useFormNavigate,
  useGetCarFeatures,
  useVehicleDocumentSync,
  VehicleForm as VFKit,
} from '@dms/features/vehicle-forms-kit';
import i18n from '@dms/i18n';
import {
  formatDocumentTitle,
  handleApiError,
  useMandatoryFields,
  FieldAvailabilityContext,
} from '@dms/shared';
import {AuditDataAssetsV2Body} from '@dms/teas';

import {SaleVehicleForm} from '../components/SaleVehicleForm';
import {DEFAULT_VEHICLE_TYPE} from '../constants/vehicleConstants';
import {useConditionDataSubmit} from '../hooks/useConditionDataSubmit';
import {useCreatePurchaseVehicle} from '../hooks/useCreatePurchaseVehicle';

const getTargetAlbum = (
  targetAlbum: unknown
): 'PHOTOS_CAR_SALES' | 'PHOTOS_CAR_PURCHASE' | null => {
  if (Array.isArray(targetAlbum) && targetAlbum.length > 0) {
    const value = targetAlbum[0];
    if (value === 'PHOTOS_CAR_SALES' || value === 'PHOTOS_CAR_PURCHASE') {
      return value;
    }
  }
  return 'PHOTOS_CAR_PURCHASE';
};

const buildRegistrationPayload = (values: SaleVehicle) => ({
  salePresetId: values.salePresetId,
  vehicleSourceCodeId: values.vehicleSourceCodeId,
  photos: values.photos?.map(({fileId}) => ({fileId})) ?? [],
  targetAlbum: values.photos?.length ? getTargetAlbum(values.targetAlbum) : null,
});

export function CreateSaleVehicle() {
  const {navigateOnConfirm, navigateOnDiscard, isBusinessCaseReferrer, getPurchaseTypeFromQuery} =
    useFormNavigate();

  const {
    generateSchema,
    isMandatoryFieldsEnabled,
    isLoading: isMandatoryFieldsLoading,
  } = useMandatoryFields();

  const currentContextRef = useRef<FieldAvailabilityContext>({
    vehicleType: DEFAULT_VEHICLE_TYPE,
  });

  const [formSchema, setFormSchema] = useState<ZodSchema<SaleVehicle>>(() =>
    generateSchema(SaleVehicleValidationSchema, currentContextRef.current)
  );

  useEffect(() => {
    setFormSchema(generateSchema(SaleVehicleValidationSchema, currentContextRef.current));
  }, [generateSchema]);

  const [getCarFeatures] = useGetCarFeatures();

  const [createVehicleMutation, {isLoading: isCreating}] = useCreateVehicleMutation();
  const [registerVehicleForSaleMutation] = useRegisterVehicleForSaleMutation();
  const [registerVehicleToBuyMutation] = useRegisterVehicleToBuyMutation();
  const {attachToBusinessCase} = useCreatePurchaseVehicle();

  const {handleSubmit: handleConditionDataSubmit} = useConditionDataSubmit();

  const {handleSaveNote} = useAdditionalNote();
  const {syncDocuments} = useVehicleDocumentSync();

  const handleVehicleTypeChange = useCallback(
    (type: VehicleTypeEnum, condition?: ConditionEnum) => {
      currentContextRef.current = {...currentContextRef.current, vehicleType: type};
      const newSchema = generateSchema(
        SaleVehicleValidationSchema,
        currentContextRef.current,
        condition
      );
      setFormSchema(newSchema);
    },
    [generateSchema]
  );

  const handleFuelTypeChange = useCallback(
    (fuelTypeCondition: FieldAvailabilityContext) => {
      currentContextRef.current = {...currentContextRef.current, ...fuelTypeCondition};
      const newSchema = generateSchema(SaleVehicleValidationSchema, currentContextRef.current);
      setFormSchema(newSchema);
    },
    [generateSchema]
  );

  const handleSave = async (values: SaleVehicle) => {
    const sourcingData = await getCarFeatures(values);
    const apiPayload = toAPIVehicle(values, null, sourcingData);

    const redirect = async (id: string) => {
      if (isBusinessCaseReferrer) {
        await attachToBusinessCase(id);
      }
      navigateOnConfirm(id);
    };

    const onCreate = () =>
      createVehicleMutation({
        createVehicleRequestBody: apiPayload,
        validateMandatoryFields: isMandatoryFieldsEnabled(values.condition?.[0] ?? undefined),
      })
        .unwrap()
        .then((createdVehicle) => createdVehicle.id)
        // Determine registration type: use "register-to-buy" endpoint for purchase
        // business cases (when referrer is BC and purchaseType query param exists),
        // otherwise use "register-for-sale" endpoint
        .then(async (id) => {
          const purchaseType = getPurchaseTypeFromQuery();
          const isForPurchase = isBusinessCaseReferrer && !!purchaseType;

          const registrationPayload = buildRegistrationPayload(values);

          if (isForPurchase) {
            await registerVehicleToBuyMutation({
              vehicleId: id,
              registerVehicleToBuyRequestBody: registrationPayload,
            }).unwrap();
          } else {
            await registerVehicleForSaleMutation({
              vehicleId: id,
              registerVehicleForSaleV2RequestBody: registrationPayload,
            }).unwrap();
          }

          return id;
        })
        // Handle additional note
        .then(async (id) => {
          if (values.additionalNote) {
            await handleSaveNote(id, values.additionalNote);
          }
          return id;
        })
        // Handle condition data
        .then(async (id) => {
          await handleConditionDataSubmit(
            id,
            omit(['sessionId'], values.auditData ?? {}) ?? {},
            (values.auditAssets as Record<string, AuditDataAssetsV2Body>) ?? {}
          );
          return id;
        })
        // Sync battery certificate document
        .then(async (id) => {
          await syncDocuments({
            vehicleId: id,
            contextTarget: 'vehicle',
            fileFields: [
              {
                fieldValue: values.batteryCertificateDocument,
                documentNote: i18n.t('entity.vehicle.labels.batteryCertificate'),
              },
            ],
          });
          return id;
        })
        .then(redirect);

    // eslint-disable-next-line no-restricted-syntax
    return onCreate().catch(handleApiError);
  };

  return (
    <>
      <Helmet>
        <title>
          {formatDocumentTitle(
            i18n.t('entity.vehicle.labels.createVehicle'),
            i18n.t('entity.vehicle.labels.pageTitle')
          )}
        </title>
      </Helmet>
      <DataStatus isLoading={isMandatoryFieldsLoading}>
        <VFKit.Form
          showMandatoryFields
          mode="onBlur"
          experimentalZodSchema={formSchema}
          defaultValues={{
            targetAlbum: ['PHOTOS_CAR_PURCHASE'],
            type: [DEFAULT_VEHICLE_TYPE],
            dimensions: {
              length: null,
              height: null,
              width: null,
            },
            dimensionsLoadingSpace: {
              length: null,
              width: null,
              height: null,
            },
          }}
        >
          <SaleVehicleForm
            onSave={handleSave}
            onVehicleTypeChange={handleVehicleTypeChange}
            onFuelTypeChange={handleFuelTypeChange}
            onDiscard={navigateOnDiscard}
            isCreating={isCreating}
          />
        </VFKit.Form>
      </DataStatus>
    </>
  );
}
